'use client';

import Link from 'next/link';
import { ChevronRight, Image as ImageIcon, Music, Video } from 'lucide-react';
import { useBasePath, useLocalHref } from '../navigation/base-path';
import { S3Image } from './s3-image';
import { TrendVideoMedia } from './trend-video-media';
import { cn } from '../utils/cn';

interface HomeScreenCreateButton {
  type: 'image' | 'video' | 'music';
  icon: typeof ImageIcon;
  label: string;
  color: string;
}

const createButtons: HomeScreenCreateButton[] = [
  {
    type: 'image',
    icon: ImageIcon,
    label: 'Картинка',
    color: 'bg-blue-500',
  },
  {
    type: 'video',
    icon: Video,
    label: 'Видео',
    color: 'bg-purple-500',
  },
  {
    type: 'music',
    icon: Music,
    label: 'Музыка',
    color: 'bg-pink-500',
  },
] as const;

export interface HomeScreenTrendExample {
  imageUrl: string;
  thumbnailUrl?: string | null;
  isVideo: boolean;
}

export interface HomeScreenTrend {
  id: string;
  name: string;
  examples: HomeScreenTrendExample[];
}

interface HomeScreenProps {
  trends?: HomeScreenTrend[];
  categories?: string[];
  musicEnabled?: boolean;
  onCreate: (type: 'image' | 'video' | 'music') => void;
  onOpenTrend: (trendId: string) => void;
}

export function HomeScreen({ trends, categories, musicEnabled = false, onCreate, onOpenTrend }: HomeScreenProps) {
  const basePath = useBasePath();
  const trendsHref = useLocalHref('/trends');

  return (
    <div className="space-y-6 p-4">
      <section>
        <h2 className="mb-3 text-lg font-semibold">Создать</h2>
        <div className="grid grid-cols-3 gap-3">
          {createButtons.map((button) => {
            const Icon = button.icon;
            const disabled = button.type === 'music' && !musicEnabled;
            return (
              <button
                key={button.type}
                type="button"
                onClick={() => {
                  if (disabled) return;
                  onCreate(button.type);
                }}
                className={cn(
                  'flex flex-col items-center justify-center gap-2 rounded-2xl p-4 transition-transform active:scale-95',
                  button.color,
                  disabled && 'pointer-events-none opacity-50',
                )}
              >
                <Icon className="h-8 w-8 text-white" />
                <span className="text-sm font-medium text-white">
                  {button.label}
                </span>
                {disabled && (
                  <span className="text-xs text-white/70">Скоро</span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Тренды</h2>
          <Link
            href={trendsHref}
            className="flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            Все
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
          {trends?.map((trend) => (
            <button
              key={trend.id}
              type="button"
              onClick={() => onOpenTrend(trend.id)}
              className="flex-shrink-0"
            >
              <div className="relative h-40 w-32 overflow-hidden rounded-2xl bg-secondary">
                {trend.examples[0] &&
                  (trend.examples[0].isVideo ? (
                    <TrendVideoMedia
                      src={trend.examples[0].imageUrl}
                      poster={trend.examples[0].thumbnailUrl}
                      alt={trend.name}
                    />
                  ) : (
                    <S3Image
                      src={trend.examples[0].imageUrl}
                      alt={trend.name}
                      fill
                      className="object-cover"
                    />
                  ))}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <p className="line-clamp-2 text-xs font-medium text-white">
                    {trend.name}
                  </p>
                </div>
              </div>
            </button>
          ))}

          {!trends && (
            <>
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="h-40 w-32 flex-shrink-0 animate-pulse rounded-2xl bg-secondary"
                />
              ))}
            </>
          )}
        </div>
      </section>

      {categories && categories.length > 0 && (
        <section>
          <h2 className="mb-3 text-lg font-semibold">Категории</h2>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Link
                key={category}
                href={`${basePath}/trends?category=${category}`}
                className="rounded-full bg-secondary px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary/80"
              >
                {category}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
